import styles from './NavItem.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import React from 'react';

import { ContentType, LinkSet, NavigationItem, as } from 'src/api/contentful/contentTypes.cloverdotcom';
import { DrawerContent } from 'src/components/DrawerContent/DrawerContent';
import { NavTitle } from 'src/components/NavTitle/NavTitle';
import { getField, isEntry } from 'src/utils/EntryUtil';

import { NavDrawer } from '../NavDrawer/NavDrawer';

export type LIProps<T> = React.AllHTMLAttributes<T>;
export interface INavItem<T extends HTMLLIElement = HTMLLIElement>
extends Pick<LIProps<T>, Exclude<keyof LIProps<T>, 'onMouseEnter' | 'onMouseLeave' | 'onTouchStart' | 'onKeyUp'>> {
  index: number;
  entry?: ReferencedEntry<NavigationItem>;
  active?: boolean;
  open?: boolean;
  onOpenDrawer?: (index: number) => void;
  onCloseDrawer?: (index: number, deactivate: boolean) => void;
  onActivate?: (index: number) => void;
  mobile?: boolean;
  tablet?: boolean;
  desktop?: boolean;
}

export class NavItem<T extends HTMLLIElement = HTMLLIElement> extends React.PureComponent<INavItem<T>> {

  static defaultProps: INavItem<HTMLLIElement> = {
    index: -1,
  };

  readonly el = React.createRef<T>();
  readonly wrapperOutRef = React.createRef<any>();
  readonly displayEl = React.createRef<HTMLButtonElement | HTMLAnchorElement>();

  readonly drawer = React.createRef<HTMLDivElement>();

  private hasDrawer: boolean = false;

  componentDidUpdate(prevProps: Readonly<INavItem>) {
    this.toggleFocus(!!prevProps.active, !!prevProps.open);
  }

  componentDidMount() {
    window.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClickOutside);
  }

  render() {
    const {
      entry,
      index,
      children,
      active,
      open,
      onOpenDrawer,
      onCloseDrawer,
      onActivate,
      className,
      mobile,
      tablet,
      desktop,
      ...props
    } = this.props;

    if (!isEntry(entry)) return null;

    this.hasDrawer = getField(as<LinkSet>(ContentType.LINK_SET, entry), 'items', []).length > 0;

    return (
      <li
        className={cx(styles.navItem, className, {
          [styles.navItemActive]: active,
        })}
        onMouseOver={() => this.displayEl.current && this.displayEl.current.blur()}
        // use touchEnd prevent touch to click conversion
        // touchEnd is cancelable, touchStart is not
        onTouchEnd={this.onTouch}
        onKeyUp={this.onKey}
        {...props}
        aria-haspopup={this.hasDrawer ? true : undefined}
        aria-expanded={open ? true : undefined}
        ref={this.wrapperOutRef}
      >
        <NavTitle entry={entry} showCaret expanded={!!open} isMobile={mobile} className={styles.title} onClick={this.onClick} onFocus={this.onFocus} ref={this.displayEl} />
        {this.hasDrawer && (
          <NavDrawer
            open={open}
            ref={this.drawer}
            onClick={this.onDrawerClick}
          >
            <DrawerContent
              active={open}
              entry={entry}
              {...{ mobile, tablet, desktop }}
            />
          </NavDrawer>
        )}
      </li>
    );
  }

  toggleFocus(prevActive: boolean, prevOpen: boolean) {
    if (!this.displayEl.current) return;

    // from inactive to active
    // or bring focus back to active item when drawer is closed
    if (!prevActive && this.props.active || this.props.active && prevOpen && !this.props.open) {
      this.displayEl.current.focus();
    }

    // from active to inactive
    if (prevActive && !this.props.active) this.displayEl.current.blur();
  }

  toggle() {
    const { open } = this.props;

    if (open) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  activate() {
    const { index, onActivate, active } = this.props;

    if (!active && onActivate) onActivate(index);
  }

  openDrawer() {
    if (!this.hasDrawer) return;

    const { index, onOpenDrawer, open } = this.props;

    if (!open && onOpenDrawer) onOpenDrawer(index);
  }

  closeDrawer(deactivate: boolean = false) {
    if (!this.hasDrawer) return;

    const { index, onCloseDrawer, open } = this.props;

    if (open && onCloseDrawer) onCloseDrawer(index, deactivate);
  }

  readonly onClick: React.MouseEventHandler<HTMLElement> = e => {
    if (e.isDefaultPrevented() || e.isPropagationStopped()) return;
    this.toggle();
  };

  readonly onFocus: React.FocusEventHandler<T> = e => {
    if (e.isPropagationStopped()) return;
    this.activate();
  };

  readonly onKey: React.KeyboardEventHandler<T> = e => {
    const isTargetContained = e.target instanceof Node && e.currentTarget.contains(e.target);

    if (e.currentTarget !== e.target && !isTargetContained) return;

    switch (e.key) {
      case ' ':
      case 'Enter':
        if (!this.props.open) {
          this.openDrawer();
          e.preventDefault();
        }
        return;
      case 'Escape':
      case 'Esc':
        if (this.props.open) {
          this.closeDrawer();
          e.preventDefault();
        }
        return;
      default:
        return;
    }
  };

  readonly onTouch: React.TouchEventHandler<T> = e => {
    this.activate();

    const isTargetContained = e.target instanceof Node && e.currentTarget.contains(e.target);

    if (e.currentTarget !== e.target && !isTargetContained) return;

    // touch drawer check
    const drawerEl = this.drawer.current;
    if (drawerEl) {
      const isTargetContainedByDrawer = e.target instanceof Node && drawerEl.contains(e.target);

      // if the touch came from the drawer, don't do anything (for scrolling)
      if (drawerEl === e.target || isTargetContainedByDrawer) return;
    }

    // stop mouse event
    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();

    this.toggle();
  };

  readonly onDrawerClick: React.MouseEventHandler = e => {
    if (e.target instanceof HTMLButtonElement || e.target instanceof HTMLAnchorElement ||
      e.target instanceof HTMLHeadingElement || e.target instanceof HTMLParagraphElement ||
      e.target instanceof HTMLDivElement || e.target instanceof HTMLSpanElement) {
      this.closeDrawer(true);
    }
  };

  readonly handleClickOutside = (e: any) => {
    const wrapperOutRef = this.wrapperOutRef.current;
    if (wrapperOutRef?.contains(e.target)) return;

    this.closeDrawer(true);
  };
}
